import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { PanelLeftOpen, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { useState, useEffect, useRef } from "react";
import { getISOWeek, getISOWeekYear } from "date-fns";
import { useAuth } from "@/lib/auth-context";
import { gravarRolagem, consumirRolagem } from "@/lib/rolagem";
import { urlAtual } from "@/lib/navegacao";
import { Eye, LogOut, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSairSuporte } from "@/hooks/use-plataforma";
import { useAcessosSuporte, useResponderAcessoSuporte } from "@/hooks/use-acessos-suporte";

interface AppLayoutProps {
  children: React.ReactNode;
}

function CurrentWeekDisplay() {
  const [currentWeek, setCurrentWeek] = useState<{ year: number; week: number } | null>(null);

  useEffect(() => {
    const now = new Date();
    setCurrentWeek({
      year: getISOWeekYear(now),
      week: getISOWeek(now)
    });
  }, []);

  if (!currentWeek) return null;
  
  return (
    <div className="flex items-center gap-1 text-sm font-semibold text-primary" data-testid="current-week">
      <Calendar className="h-4 w-4" />
      <span>Semana S{currentWeek.week}</span>
    </div>
  );
}

function AppHeader() {
  const { setOpenMobile } = useSidebar();
  const { user } = useAuth();

  return (
    <header className="flex items-center justify-between px-3 py-2 md:p-4 border-b flex-shrink-0 gap-3">
      <div className="lg:hidden">
        <h1 className="text-base md:text-lg font-bold leading-tight">Rizoma Farm</h1>
        {user?.orgName && (
          <p className="text-xs text-muted-foreground leading-tight truncate max-w-[160px]">
            {user.orgName}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 ml-auto">
        <CurrentWeekDisplay />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpenMobile(true)}
          data-testid="button-sidebar-toggle"
          className="h-10 w-10 lg:hidden"
        >
          <PanelLeftOpen className="h-6 w-6" />
        </Button>
      </div>
    </header>
  );
}

/**
 * O BANNER do modo suporte ("Entrar como", P2): fixo no topo, diz de quem é a
 * tela e que é só leitura, e tem o Sair. `document.title` ganha o prefixo
 * "[Suporte]" para a aba do navegador também avisar.
 */
function BannerSuporte() {
  const { user } = useAuth();
  const sair = useSairSuporte();
  const { toast } = useToast();
  const orgNome = user?.suporte?.orgNome ?? user?.orgName ?? "esta fazenda";
  const orgId = user?.orgId;

  useEffect(() => {
    if (!user?.suporte) return;
    const original = document.title;
    if (!original.startsWith("[Suporte]")) document.title = `[Suporte] ${original}`;
    return () => { document.title = original; };
  }, [user?.suporte]);

  if (!user?.suporte) return null;

  function handleSair() {
    sair.mutate(undefined, {
      onSuccess: () => { window.location.assign(orgId ? `/plataforma/fazendas/${orgId}` : "/plataforma/fazendas"); },
      onError: (e: Error) => toast({ title: "Não deu para sair", description: e.message, variant: "destructive" }),
    });
  }

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-atencao-suave text-atencao text-sm border-b" data-testid="banner-suporte">
      <div className="flex items-center gap-2 min-w-0">
        <Eye className="h-4 w-4 shrink-0" />
        <span className="truncate">
          Você está vendo <strong>{orgNome}</strong> como suporte — somente leitura
        </span>
      </div>
      <Button size="sm" variant="outline" onClick={handleSair} disabled={sair.isPending} data-testid="button-sair-suporte">
        <LogOut className="h-4 w-4 mr-1" />Sair
      </Button>
    </div>
  );
}

/**
 * O CARTÃO do ADMIN da org: pedido de acesso do suporte aguardando decisão, ou
 * acesso vigente com o Encerrar. Só quem gere usuários vê (o hook decide).
 */
function CartaoAcessoSuporte() {
  const acessos = useAcessosSuporte();
  const responder = useResponderAcessoSuporte();
  const { toast } = useToast();
  // ⚠️ `Array.isArray`, não `?? []` — que só defende de null/undefined.
  //
  // Reproduzido no bundle de produção (22/09) servindo `{}` neste endpoint: o
  // `.map` estoura, o ErrorBoundary do `main.tsx` envolve o `<App/>` inteiro, e
  // a tela vira "Algo deu errado ao carregar esta tela" — SEM sidebar, em
  // qualquer rota, porque este cartão mora no layout. É o mesmo retrato do que
  // apareceu em /mercado/produtores no dia 13/09, cuja causa nunca foi
  // confirmada; aqui o buraco é real e o custo de fechá-lo é uma linha.
  const lista = Array.isArray(acessos.data) ? acessos.data : [];
  if (lista.length === 0) return null;

  const agir = (id: string, acao: "autorizar" | "recusar" | "encerrar") =>
    responder.mutate({ id, acao }, {
      onSuccess: () => toast({ title: acao === "autorizar" ? "Acesso autorizado" : acao === "recusar" ? "Pedido recusado" : "Acesso encerrado" }),
      onError: (e: Error) => toast({ title: "Não deu", description: e.message, variant: "destructive" }),
    });

  return (
    <div className="space-y-2 px-3 pt-3 md:px-8 md:pt-4" data-testid="cartao-acesso-suporte">
      {lista.map(a => a.status === "PENDENTE" ? (
        <div key={a.id} className="rounded-lg border border-atencao/30 bg-atencao-suave p-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between" data-testid={`acesso-pendente-${a.id}`}>
          <div className="min-w-0 text-sm">
            <p className="font-medium flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />O suporte do Rizoma pede acesso de leitura por {a.duracaoHoras} h</p>
            <p className="text-muted-foreground">Motivo: {a.motivo}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => agir(a.id, "recusar")} disabled={responder.isPending} data-testid={`button-recusar-${a.id}`}>Recusar</Button>
            <Button size="sm" onClick={() => agir(a.id, "autorizar")} disabled={responder.isPending} data-testid={`button-autorizar-${a.id}`}>Autorizar</Button>
          </div>
        </div>
      ) : (
        <div key={a.id} className="rounded-lg border p-2.5 flex items-center justify-between gap-3 text-xs text-muted-foreground" data-testid={`acesso-vigente-${a.id}`}>
          <span className="flex items-center gap-1.5 min-w-0 truncate">
            <Eye className="h-3.5 w-3.5 shrink-0" />
            Suporte Rizoma com acesso de leitura até {a.expiraEm ? new Date(a.expiraEm).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
          <Button size="sm" variant="ghost" onClick={() => agir(a.id, "encerrar")} disabled={responder.isPending} data-testid={`button-encerrar-${a.id}`}>Encerrar</Button>
        </div>
      ))}
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };
  const mainRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number | null>(null);

  // Regra 3 — rolagem lembrada por URL. O scroll é deste `<main>`, não da
  // janela, e o AppLayout remonta a cada rota: sem isto o "Voltar" devolve a
  // lista com os filtros certos, mas no topo.
  useEffect(() => {
    const alvo = consumirRolagem(urlAtual());
    if (alvo === undefined || alvo === 0) return;
    // Dois frames: o primeiro deixa o React comitar a lista (que vem do cache do
    // React Query, `staleTime: Infinity`, e renderiza síncrona), o segundo
    // aplica o scroll sobre a altura já real. Se a lista ainda estiver em
    // `isLoading` (cache frio depois de reload), a altura não dá e nada acontece
    // — sem MutationObserver, sem retry: a Regra 3 é conforto, não invariante.
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        const el = mainRef.current;
        if (el) el.scrollTop = alvo;
      });
    });
    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, []);

  // Throttle por rAF: `scroll` dispara dezenas de vezes por gesto no celular e
  // gravar em todas seria trabalho jogado fora.
  function handleScroll() {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = mainRef.current;
      if (el) gravarRolagem(urlAtual(), el.scrollTop);
    });
  }

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-x-hidden">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-h-0 overflow-x-hidden">
          <BannerSuporte />
          <AppHeader />
          <CartaoAcessoSuporte />
          <main
            ref={mainRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden p-3 md:p-8 max-w-full"
          >
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
